import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import _ from 'lodash';
import './NotificationsPreview.scss';

import { renderEmailTemplate, sendTestEmail, sendTestPush, fireTestEvent, searchBrandsForPreview } from '../../api/notifications';
import { fetchUsers } from '../../api/users';
import PushPreview from './elements/PushPreview';
import EntityAvatar from './elements/EntityAvatar';
import EventPreview from './elements/EventPreview';
import ReceiptsPanel from './elements/ReceiptsPanel';
import SmsPreview from './elements/SmsPreview';
import { humanizeStatus, COPY_STATUS_LABELS } from '../../helpers/notification_registry_helpers';

const PRESETS = [
  { key: 'minimal', label: 'Minimal' },
  { key: 'full', label: 'Full' }
];

const DEBOUNCE_MS = 300;
const BRAND_FIELDS = new Set(['brand', 'messageBrand']);
const USER_FIELDS = new Set(['user']);

const humanizeName = name => name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const serializeFieldValue = val => {
  if (val === null || val === undefined) return '';
  if (typeof val === 'object') return JSON.stringify(val, null, 2);
  return String(val);
};

const deserializeFieldValue = (str, originalType) => {
  if (str === '') return originalType === 'string' ? '' : null;
  if (originalType === 'number') {
    const num = Number(str);
    return isNaN(num) ? str : num;
  }
  if (originalType === 'boolean') return str === 'true';
  if (originalType === 'object' || originalType === 'array') {
    try {
      return JSON.parse(str);
    } catch {
      return str;
    }
  }
  return str;
};

const getFieldType = val => {
  if (val === null || val === undefined) return 'string';
  if (Array.isArray(val)) return 'array';
  return typeof val;
};

// Shared search + chip component for user and brand lookups
const EntitySearch = ({ selected, onSelect, onClear, searchFn, placeholder, renderRow, renderChip, wrapperRef }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const containerRef = useRef(null);

  const debouncedSearch = useCallback(
    _.debounce(async q => {
      if (!q.trim()) {
        setResults([]);
        setIsSearching(false);
        return;
      }
      try {
        const data = await searchFn(q);
        setResults(data);
      } catch {
        setResults([]);
      } finally {
        setIsSearching(false);
      }
    }, DEBOUNCE_MS),
    [searchFn]
  );

  const handleChange = value => {
    setQuery(value);
    setIsSearching(true);
    setShowDropdown(true);
    debouncedSearch(value);
  };

  const handleSelect = item => {
    onSelect(item);
    setQuery('');
    setResults([]);
    setShowDropdown(false);
  };

  useEffect(() => {
    const handleClickOutside = e => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (selected) return renderChip(selected, onClear);

  return (
    <div
      className='entity-lookup'
      ref={el => {
        containerRef.current = el;
        if (wrapperRef) wrapperRef.current = el;
      }}
    >
      <input
        type='text'
        className='entity-input'
        placeholder={placeholder}
        value={query}
        onChange={e => handleChange(e.target.value)}
        onFocus={() => query && setShowDropdown(true)}
        onKeyDown={e => {
          if (e.key === 'Enter' && results.length > 0) handleSelect(results[0]);
          if (e.key === 'Escape') {
            setShowDropdown(false);
            e.target.blur();
          }
        }}
      />
      {showDropdown && query.trim().length > 0 && (
        <div className='entity-dropdown'>
          {isSearching ? (
            <div className='dropdown-empty'>Searching...</div>
          ) : results.length === 0 ? (
            <div className='dropdown-empty'>No results</div>
          ) : (
            results.map(item => renderRow(item, () => handleSelect(item)))
          )}
        </div>
      )}
    </div>
  );
};

EntitySearch.propTypes = {
  selected: PropTypes.object,
  onSelect: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  searchFn: PropTypes.func.isRequired,
  placeholder: PropTypes.string.isRequired,
  renderRow: PropTypes.func.isRequired,
  renderChip: PropTypes.func.isRequired,
  wrapperRef: PropTypes.object
};

const NotificationsPreview = props => {
  const { template, initialUser, onBack, onOpenRegistryEntry } = props;
  const { name, channels } = template;

  const [activePreset, setActivePreset] = useState('full');
  const [renderData, setRenderData] = useState(null);
  const [isRendering, setIsRendering] = useState(true);
  const [showRawHtml, setShowRawHtml] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [showFields, setShowFields] = useState(true);
  const [showPayload, setShowPayload] = useState(true);
  const [viewportMode, setViewportMode] = useState('desktop');
  const iframeRef = useRef(null);

  // Lookup state
  const [selectedUser, setSelectedUser] = useState(initialUser || null);
  const [selectedBrand, setSelectedBrand] = useState(null);

  // Field editor state
  const [baselineData, setBaselineData] = useState(null);
  const [fieldValues, setFieldValues] = useState({});
  const [fieldTypes, setFieldTypes] = useState({});
  const [parseErrors, setParseErrors] = useState({});

  const hasEmail = channels.includes('email');
  const hasPush = channels.includes('push');
  const hasSms = channels.includes('sms');
  const hasEvent = channels.includes('klaviyo');
  const requiredFields = new Set(template.required || []);
  const showUserLookup = [...USER_FIELDS].some(f => requiredFields.has(f));
  const showBrandLookup = [...BRAND_FIELDS].some(f => requiredFields.has(f));
  const isLive = !!selectedUser || !!selectedBrand;
  const hasFullLiveData = renderData?.hasFullLiveData && !!selectedUser;

  const channelTabs = useMemo(() => {
    const tabs = [];
    if (hasEmail) tabs.push({ key: 'email', label: 'Email' });
    if (hasPush) tabs.push({ key: 'push', label: 'Push' });
    if (hasSms) tabs.push({ key: 'sms', label: 'SMS' });
    if (hasEvent) tabs.push({ key: 'event', label: 'Event' });
    return tabs;
  }, [hasEmail, hasPush, hasSms, hasEvent]);
  const [activeChannel, setActiveChannel] = useState(channelTabs[0]?.key || 'email');

  // Search functions
  const searchUsers = useCallback(async q => {
    const data = await fetchUsers({ search: q, limit: 8 });
    return data.users || [];
  }, []);

  const searchBrands = useCallback(async q => {
    const data = await searchBrandsForPreview(q);
    return data.brands || [];
  }, []);

  const initFieldsFromMockData = useCallback(mockData => {
    if (!mockData) return;
    setBaselineData(mockData);
    const values = {};
    const types = {};
    for (const [key, val] of Object.entries(mockData)) {
      values[key] = serializeFieldValue(val);
      types[key] = getFieldType(val);
    }
    setFieldValues(values);
    setFieldTypes(types);
    setParseErrors({});
  }, []);

  const fetchRender = useCallback(
    (preset, customData) => {
      setIsRendering(true);
      const body = {};
      if (customData) body.customData = customData;
      else body.mockPreset = preset || activePreset;
      if (selectedUser) body.username = selectedUser.username;
      if (selectedBrand) body.Brand_id = selectedBrand.id;

      renderEmailTemplate(name, body)
        .then(resp => {
          setRenderData(resp);
          if (resp?.mockData && !customData) initFieldsFromMockData(resp.mockData);
        })
        .catch(() => window.ALERT?.error?.('Failed to render template.'))
        .finally(() => setIsRendering(false));
    },
    [name, selectedUser, selectedBrand, activePreset, initFieldsFromMockData]
  );

  // Re-render when preset or selections change
  useEffect(() => {
    fetchRender(activePreset);
  }, [activePreset, selectedUser, selectedBrand]);

  const handleFieldChange = (key, value) => {
    setFieldValues(prev => ({ ...prev, [key]: value }));
    if (parseErrors[key]) setParseErrors(prev => _.omit(prev, key));
  };

  const assembleCustomData = () => {
    const data = {};
    const errors = {};
    for (const [key, strVal] of Object.entries(fieldValues)) {
      const type = fieldTypes[key];
      if ((type === 'object' || type === 'array') && strVal.trim()) {
        try {
          data[key] = JSON.parse(strVal);
        } catch (e) {
          errors[key] = e.message;
        }
      } else {
        data[key] = deserializeFieldValue(strVal, type);
      }
    }
    return { data, errors };
  };

  const handleRerender = () => {
    if (isRendering) return;
    const { data, errors } = assembleCustomData();
    if (Object.keys(errors).length) {
      setParseErrors(errors);
      window.ALERT?.error?.(`Invalid JSON in ${Object.keys(errors).join(', ')}`);
      return;
    }
    setParseErrors({});
    fetchRender(null, data);
  };

  const handleReset = () => {
    if (baselineData) initFieldsFromMockData(baselineData);
  };

  const modifiedFields = useMemo(() => {
    if (!baselineData) return new Set();
    const modified = new Set();
    for (const [key, val] of Object.entries(fieldValues)) {
      if (serializeFieldValue(baselineData[key]) !== val) modified.add(key);
    }
    return modified;
  }, [fieldValues, baselineData]);

  const sortedFieldKeys = useMemo(() => {
    return Object.keys(fieldValues).sort((a, b) => {
      const aReq = requiredFields.has(a) ? 0 : 1;
      const bReq = requiredFields.has(b) ? 0 : 1;
      if (aReq !== bReq) return aReq - bReq;
      return a.localeCompare(b);
    });
  }, [fieldValues, requiredFields]);

  const handleKeyDown = e => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleRerender();
    }
  };

  const writeIframe = iframe => {
    if (!iframe || !renderData?.html) return;
    iframeRef.current = iframe;
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) return;
    doc.open();
    doc.write(`<html><head><style>body { margin: 0; font-family: sans-serif; }</style></head><body>${renderData.html}</body></html>`);
    doc.close();
  };

  const handleCopyHtml = () => {
    if (!renderData?.html) return;
    navigator.clipboard
      .writeText(renderData.html)
      .then(() => window.ALERT?.success?.('HTML copied to clipboard.'))
      .catch(() => window.ALERT?.error?.('Failed to copy.'));
  };

  const handleCopyEvent = () => {
    if (!renderData?.event) return;
    navigator.clipboard
      .writeText(JSON.stringify(renderData.event, null, 2))
      .then(() => window.ALERT?.success?.('Event payload copied to clipboard.'))
      .catch(() => window.ALERT?.error?.('Failed to copy.'));
  };

  const handleSendTestEmail = () => {
    if (isSending || !renderData?.html || !renderData?.subject) return;
    setIsSending(true);
    sendTestEmail(name, { subject: renderData.subject, html: renderData.html })
      .then(resp => window.ALERT?.success?.(`Test email sent to ${resp.email}`))
      .catch(err => window.ALERT?.error?.(err || 'Failed to send test email.'))
      .finally(() => setIsSending(false));
  };

  // The server re-renders from the same data the preview shows; the destination is always the admin pressing the button.
  const testSendBody = () => ({
    customData: renderData?.mockData,
    ...(selectedUser ? { username: selectedUser.username } : {}),
    ...(selectedBrand ? { Brand_id: selectedBrand.id } : {})
  });

  const handleSendTestPush = () => {
    if (isSending || !renderData?.push || renderData.push.error) return;
    setIsSending(true);
    sendTestPush(name, testSendBody())
      .then(resp => window.ALERT?.success?.(`Test push sent to ${resp.destination}.`))
      .catch(err => window.ALERT?.error?.(err || 'Failed to send the test push.'))
      .finally(() => setIsSending(false));
  };

  const canFireTestEvent = !renderData?.event?.error && typeof renderData?.event?.eventName === 'string' && !!renderData.event.eventName.trim();

  const handleFireTestEvent = () => {
    if (isSending || !canFireTestEvent) return;
    setIsSending(true);
    fireTestEvent(name, testSendBody())
      .then(resp => window.ALERT?.success?.(`${resp.eventName} fired on ${resp.destination}.`))
      .catch(err => window.ALERT?.error?.(err || 'Failed to fire the test event.'))
      .finally(() => setIsSending(false));
  };

  const metadataFields = [
    { label: 'Template Name', value: name },
    { label: 'Subject', value: renderData?.subject || '—' },
    { label: 'Channels', value: (renderData?.channels || channels).join(', ') },
    { label: 'Required Fields', value: (renderData?.required || template.required)?.join(', ') || 'None' },
    { label: 'Notification Setting', value: renderData?.userNotificationSetting || template.userNotificationSetting || '—' },
    { label: 'Throttle (min)', value: renderData?.throttleDurationMin || template.throttleDurationMin || '—' },
    {
      label: 'Block Email if Push Succeeds',
      value: renderData?.blockEmailToUserIfPushSucceeds ?? template.blockEmailToUserIfPushSucceeds ? 'Yes' : 'No'
    },
    { label: 'Budget Category', value: template.budgetCategory || '—' },
    { label: 'Notification Center', value: template.notificationCenterBehavior || '—' },
    { label: 'Caps That Apply', value: (template.capsThatApply || []).join(' ') || '—' },
    { label: 'Registry', value: <RegistryValue registry={template.registry} onOpenRegistryEntry={onOpenRegistryEntry} /> },
    { label: 'Copy Status', value: COPY_STATUS_LABELS[template.registry?.copyStatus] || '—' }
  ];

  // Render helpers for EntitySearch
  const renderUserRow = (user, onSelect) => (
    <div key={user.id} className='dropdown-row' onClick={onSelect}>
      <EntityAvatar image={user.image} name={user.name} className='dropdown-avatar' />
      <div className='dropdown-info'>
        <span className='dropdown-name'>{user.name}</span>
        <span className='dropdown-sub'>@{user.username}</span>
      </div>
    </div>
  );

  const renderUserChip = (user, onClear) => (
    <div className='entity-chip'>
      <EntityAvatar image={user.image} name={user.name} className='chip-avatar' />
      <div className='chip-info'>
        <span className='chip-name'>{user.name}</span>
        <span className='chip-sub'>@{user.username}</span>
      </div>
      {hasFullLiveData && <span className='live-badge'>Live</span>}
      <div className='chip-clear' onClick={onClear}>
        &times;
      </div>
    </div>
  );

  const renderBrandRow = (brand, onSelect) => (
    <div key={brand.id} className='dropdown-row' onClick={onSelect}>
      <EntityAvatar image={brand.logo || brand.mobileBadgeImage} name={brand.name} square className='dropdown-avatar' />
      <div className='dropdown-info'>
        <span className='dropdown-name'>{brand.name}</span>
        <span className='dropdown-sub'>{brand.domain}</span>
      </div>
    </div>
  );

  const renderBrandChip = (brand, onClear) => (
    <div className='entity-chip brand'>
      <EntityAvatar image={brand.logo || brand.mobileBadgeImage} name={brand.name} square className='chip-avatar' />
      <div className='chip-info'>
        <span className='chip-name'>{brand.name}</span>
        <span className='chip-sub'>{brand.domain}</span>
      </div>
      <div className='chip-clear' onClick={onClear}>
        &times;
      </div>
    </div>
  );

  return (
    <div className='notifications-preview-outer'>
      {/* Header */}
      <div className='preview-header'>
        <div className='header-left'>
          <div className='back-btn' onClick={onBack}>
            &larr; Back to Templates
          </div>
          <h2 className='template-name'>{humanizeName(name)}</h2>
          <div className='channel-badges'>
            {channels.map(ch => (
              <span key={ch} className={cn('channel-badge', ch)}>
                {ch}
              </span>
            ))}
          </div>
        </div>
        <div className='header-controls'>
          <div className='preset-toggle'>
            {PRESETS.map(p => (
              <div key={p.key} className={cn('preset-btn', { active: activePreset === p.key })} onClick={() => setActivePreset(p.key)}>
                {p.label}
              </div>
            ))}
          </div>

          {showUserLookup && (
            <EntitySearch
              selected={selectedUser}
              onSelect={setSelectedUser}
              onClear={() => setSelectedUser(null)}
              searchFn={searchUsers}
              placeholder='Look up user...'
              renderRow={renderUserRow}
              renderChip={renderUserChip}
            />
          )}
          {showBrandLookup && (
            <EntitySearch
              selected={selectedBrand}
              onSelect={setSelectedBrand}
              onClear={() => setSelectedBrand(null)}
              searchFn={searchBrands}
              placeholder='Look up brand...'
              renderRow={renderBrandRow}
              renderChip={renderBrandChip}
            />
          )}
        </div>
      </div>

      {/* Content */}
      <div className='preview-content'>
        <div className='preview-main'>
          {channelTabs.length > 1 && (
            <div className='channel-tab-bar'>
              {channelTabs.map(tab => (
                <div
                  key={tab.key}
                  className={cn('channel-tab', tab.key, { active: activeChannel === tab.key })}
                  onClick={() => setActiveChannel(tab.key)}
                >
                  {tab.label}
                </div>
              ))}
            </div>
          )}

          {activeChannel === 'email' && hasEmail && (
            <div className='email-section'>
              <div className='section-header'>
                <div className='section-title'>Email Preview</div>
                <div className='section-actions'>
                  <div className='viewport-toggle'>
                    {[
                      { key: 'desktop', label: 'Desktop' },
                      { key: 'mobile', label: 'Mobile' }
                    ].map(v => (
                      <div key={v.key} className={cn('viewport-btn', { active: viewportMode === v.key })} onClick={() => setViewportMode(v.key)}>
                        {v.label}
                      </div>
                    ))}
                  </div>
                  <div className={cn('action-btn', { active: showRawHtml })} onClick={() => setShowRawHtml(!showRawHtml)}>
                    {showRawHtml ? 'Preview' : 'Raw HTML'}
                  </div>
                  {renderData?.html && (
                    <div className='action-btn' onClick={handleCopyHtml}>
                      Copy HTML
                    </div>
                  )}
                  {renderData?.html && renderData?.subject && (
                    <div className={cn('action-btn send-btn', { disabled: isSending })} onClick={handleSendTestEmail}>
                      {isSending ? 'Sending...' : 'Send to me'}
                    </div>
                  )}
                </div>
              </div>
              {renderData?.subject && (
                <div className='subject-line'>
                  <span className='subject-label'>Subject:</span> {renderData.subject}
                </div>
              )}
              <div className={cn('email-body', { rendering: isRendering, mobile: viewportMode === 'mobile' })}>
                {isRendering && !renderData ? (
                  <div className='email-loading'>
                    <div className='shimmer-bar wide' />
                    <div className='shimmer-bar' />
                    <div className='shimmer-bar narrow' />
                  </div>
                ) : showRawHtml ? (
                  <pre className='raw-html'>{renderData?.html || 'No HTML available'}</pre>
                ) : renderData?.html ? (
                  <iframe
                    ref={writeIframe}
                    key={`${name}-${activePreset}-${selectedUser?.id || 'mu'}-${selectedBrand?.id || 'mb'}-${renderData?.html?.length}`}
                    title={`${name} email preview`}
                    sandbox='allow-same-origin'
                    className='email-iframe'
                  />
                ) : (
                  <div className='email-empty'>No HTML content for this template.</div>
                )}
              </div>
            </div>
          )}

          {activeChannel === 'push' && hasPush && (
            <div className='channel-section'>
              <div className='section-header'>
                <div className='section-title'>Push Preview</div>
                <div className='section-actions'>
                  {renderData?.push && !renderData.push.error && (
                    <div
                      className={cn('action-btn send-btn', { disabled: isSending })}
                      onClick={handleSendTestPush}
                      title='Sends this push, marked [Test], to every device where your admin account is logged into the ShopMy app. Files no receipt.'
                    >
                      {isSending ? 'Sending...' : 'Send to me'}
                    </div>
                  )}
                </div>
              </div>
              <div className='channel-stage'>
                {isRendering && !renderData ? <div className='channel-loading'>Loading...</div> : <PushPreview push={renderData?.push} />}
              </div>
            </div>
          )}

          {activeChannel === 'sms' && hasSms && (
            <div className='channel-section'>
              <div className='section-header'>
                <div className='section-title'>SMS Preview</div>
              </div>
              <div className='channel-stage'>
                {isRendering && !renderData ? <div className='channel-loading'>Loading...</div> : <SmsPreview sms={renderData?.sms} />}
              </div>
            </div>
          )}

          {activeChannel === 'event' && hasEvent && (
            <div className='channel-section'>
              <div className='section-header'>
                <div className='section-title'>Event Properties</div>
                <div className='section-actions'>
                  {renderData?.event && (
                    <div className='action-btn' onClick={handleCopyEvent}>
                      Copy payload
                    </div>
                  )}
                  {renderData?.event && (
                    <div
                      className={cn('action-btn send-btn', { disabled: isSending || !canFireTestEvent })}
                      onClick={handleFireTestEvent}
                      title='Fires this event on your own Klaviyo profile with a test- dedupe key. Any live flow on the event will run for you. Files no receipt.'
                    >
                      {isSending ? 'Firing...' : 'Fire test event to me'}
                    </div>
                  )}
                </div>
              </div>
              {isRendering && !renderData ? (
                <div className='channel-loading'>Loading...</div>
              ) : (
                <EventPreview event={renderData?.event} recipientLabel={selectedUser ? `@${selectedUser.username}` : 'the mock user'} />
              )}
            </div>
          )}
        </div>

        {/* Sidebar — field editor */}
        <div className='preview-sidebar'>
          {/* Mock Data Fields */}
          <div className='field-editor-section'>
            <div className='field-editor-header'>
              <div className='field-editor-title-row' onClick={() => setShowFields(!showFields)}>
                <span className={cn('expand-icon', { open: showFields })}>&rsaquo;</span>
                <span className='sidebar-title'>{isLive ? 'Live Data' : 'Mock Data'}</span>
              </div>
              <div className='field-editor-actions'>
                {modifiedFields.size > 0 && (
                  <div className='action-btn' onClick={handleReset}>
                    Reset
                  </div>
                )}
                <div className={cn('rerender-btn', { disabled: isRendering })} onClick={handleRerender} title='⌘+Enter'>
                  {isRendering ? 'Rendering...' : 'Re-render'}
                </div>
              </div>
            </div>
            {showFields && (
              <>
                {modifiedFields.size > 0 && (
                  <div className='modified-hint'>
                    {modifiedFields.size} field{modifiedFields.size !== 1 ? 's' : ''} modified &middot; {'\u2318'}+Enter to re-render
                  </div>
                )}
                <div className='field-list'>
                  {sortedFieldKeys.map(key => {
                    const type = fieldTypes[key];
                    const isComplex = type === 'object' || type === 'array';
                    const isModified = modifiedFields.has(key);
                    const isRequired = requiredFields.has(key);
                    const hasError = !!parseErrors[key];

                    return (
                      <div key={key} className={cn('field-row', { modified: isModified, 'has-error': hasError })}>
                        <div className='field-label-row'>
                          <span className='field-name'>{key}</span>
                          <span className='field-type'>{type}</span>
                          {isRequired && <span className='field-required'>req</span>}
                          {isModified && <span className='field-modified-dot' />}
                        </div>
                        {isComplex ? (
                          <textarea
                            className={cn('field-textarea', { 'has-error': hasError })}
                            value={fieldValues[key]}
                            onChange={e => handleFieldChange(key, e.target.value)}
                            onKeyDown={handleKeyDown}
                            spellCheck={false}
                            rows={Math.min(Math.max(fieldValues[key]?.split('\n').length || 1, 2), 10)}
                          />
                        ) : (
                          <input
                            className={cn('field-input', { 'has-error': hasError })}
                            type='text'
                            value={fieldValues[key]}
                            onChange={e => handleFieldChange(key, e.target.value)}
                            onKeyDown={handleKeyDown}
                            spellCheck={false}
                          />
                        )}
                        {hasError && <div className='field-error'>{parseErrors[key]}</div>}
                      </div>
                    );
                  })}
                  {sortedFieldKeys.length === 0 && <div className='field-list-empty'>Loading fields...</div>}
                </div>
              </>
            )}
          </div>

          {/* Metadata (collapsed) */}
          <MetadataPanel metadataFields={metadataFields} />

          <ReceiptsPanel receipts={template.receipts7d} />

          {activeChannel === 'push' && renderData?.push && !renderData.push.error && (
            <div className='data-section'>
              <div className='data-section-header' onClick={() => setShowPayload(!showPayload)}>
                <span className={cn('expand-icon', { open: showPayload })}>&rsaquo;</span>
                <span className='sidebar-title'>Push Payload</span>
              </div>
              {showPayload && <pre className='data-json'>{JSON.stringify(renderData.push, null, 2)}</pre>}
            </div>
          )}

          {activeChannel === 'sms' && renderData?.sms && !renderData.sms.error && (
            <div className='data-section'>
              <div className='data-section-header' onClick={() => setShowPayload(!showPayload)}>
                <span className={cn('expand-icon', { open: showPayload })}>&rsaquo;</span>
                <span className='sidebar-title'>SMS Payload</span>
              </div>
              {showPayload && <pre className='data-json'>{JSON.stringify(renderData.sms, null, 2)}</pre>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const MetadataPanel = ({ metadataFields }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className='metadata-panel'>
      <div className='metadata-panel-header' onClick={() => setOpen(!open)}>
        <span className='sidebar-title'>Template Metadata</span>
        <span className={cn('expand-icon', { open })}>&rsaquo;</span>
      </div>
      {open && (
        <div className='metadata-list'>
          {metadataFields.map(field => (
            <div key={field.label} className='metadata-row'>
              <div className='metadata-label'>{field.label}</div>
              <div className={cn('metadata-value', { mono: field.label === 'Template Name' || field.label === 'Required Fields' })}>
                {field.value}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

MetadataPanel.propTypes = {
  metadataFields: PropTypes.array.isRequired
};

// The registry entry for this template, matched by short code; "—" when the Registry has no entry for it.
const RegistryValue = ({ registry, onOpenRegistryEntry }) => {
  if (!registry) return '—';
  return (
    <span className='registry-value'>
      <span className={cn('registry-status', registry.status)}>{humanizeStatus(registry.status)}</span>
      <button type='button' className='registry-link' onClick={() => onOpenRegistryEntry(registry.shortCode)}>
        Open in Registry ↗
      </button>
    </span>
  );
};

RegistryValue.propTypes = {
  registry: PropTypes.object,
  onOpenRegistryEntry: PropTypes.func.isRequired
};

NotificationsPreview.propTypes = {
  template: PropTypes.object.isRequired,
  initialUser: PropTypes.object,
  onBack: PropTypes.func.isRequired,
  onOpenRegistryEntry: PropTypes.func.isRequired
};

export default NotificationsPreview;
